/**
 * What posthog-js registers for attribution.
 *
 * First touch comes from the `attribution` cookie and is registered once. The
 * source page, the CTA id and the forwarded referrer describe the visit a link
 * started, so they come from the landing URL and last for that session only.
 * Registered permanently, they rode on every later event: a customer's pageviews
 * kept the CTA of one old card click, and a stored `$referrer` replaces the one
 * posthog-js records per session.
 */
export type AttributionRegistration = {
  /** Passed to `register_once`: first touch, from the cookie. */
  once: Record<string, string>;
  /** Passed to `register_for_session`: this visit, from the landing URL. */
  session: Record<string, string>;
};

const referringDomain = (ref: string): string | null => {
  try {
    return new URL(/^https?:\/\//.test(ref) ? ref : `https://${ref}`).hostname;
  } catch {
    return null;
  }
};

export const buildAttributionRegistration = (
  cookie: Record<string, string>,
  landing: Record<string, string>,
): AttributionRegistration => {
  const once: Record<string, string> = {};
  const session: Record<string, string> = {};

  const initialDomain = cookie.ref ? referringDomain(cookie.ref) : null;
  if (cookie.ref && initialDomain) {
    once.$initial_referrer = cookie.ref;
    once.$initial_referring_domain = initialDomain;
  }

  if (landing.utm_content_source) {
    session.utm_content_source = landing.utm_content_source;
  }
  if (landing.cta) {
    session.cta = landing.cta;
  }
  const domain = landing.ref ? referringDomain(landing.ref) : null;
  if (landing.ref && domain) {
    session.$referrer = landing.ref;
    session.$referring_domain = domain;
  }

  return { once, session };
};
